import { Switch, tooltipClasses } from "@mui/material";
import { motion } from "motion/react";
import { ReactNode, createElement, useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TbInfoCircle } from "react-icons/tb";

import { OperationStatusItem, operationStatuses } from "@/constants/operationStatuses";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { PlanFilterStatuses } from "@/stores/use-plan-table";

import { CreatePassengerForm } from "./CreateCustomPassengerDialog";

export function CreatePassengerDestinationType({ activeType }: { activeType: PlanFilterStatuses }) {
    const { control, setValue, trigger, formState, resetField } = useFormContext<CreatePassengerForm>();

    const activeStyle = useMemo(() => {
        for (const [index, item] of operationStatuses.entries()) {
            if (item.value === activeType) return { translateX: `${index * 100}%`, backgroundColor: item.bg };
        }
        return false;
    }, [activeType]);

    const handleUpdate = useCallback(
        async (status: PlanFilterStatuses) => {
            if (![PlanFilterStatuses.ARRIVAL, PlanFilterStatuses.DEPARTURE].includes(status) || status === activeType)
                return;

            if (status === PlanFilterStatuses.ARRIVAL) {
                trigger("step2.departure").then((res) => (res ? setValue("step2.activeType", status) : void 0));
            } else {
                trigger("step2.arrival").then((res) => (res ? setValue("step2.activeType", status) : void 0));
            }
        },
        [activeType]
    );

    const filterElements = useMemo(
        () =>
            operationStatuses
                .filter((item) => item.value !== PlanFilterStatuses.CANCELLED)
                .map((item) => (
                    <StatusItem
                        key={item.tooltip.title}
                        {...item}
                        active={item.value === activeType}
                        handleUpdate={handleUpdate}
                    >
                        <Controller
                            name={item.formPath}
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    className="!ml-auto"
                                    {...field}
                                    checked={field.value === "true"}
                                    color={item.color}
                                    onChange={(e) => {
                                        resetField(
                                            item.value === PlanFilterStatuses.ARRIVAL
                                                ? "step2.arrival.info"
                                                : "step2.departure.info"
                                        );
                                        field.onChange(e.target.checked ? "true" : "false");
                                    }}
                                />
                            )}
                        />
                    </StatusItem>
                )),
        [activeType, formState]
    );

    return (
        <div className=" relative col-span-full py-1 min-h-12 font-medium grid grid-cols-2 bg-gray-100/80  divide-x divide-gray-300    rounded">
            {filterElements}
            {activeStyle && (
                <motion.div
                    animate={activeStyle}
                    transition={{ type: "tween" }}
                    className="  rounded-none absolute   !self-center  !h-[calc(100%-8px)]     !w-1/2 "
                />
            )}
        </div>
    );
}

function StatusItem(
    status: OperationStatusItem & {
        active: boolean;
        children: ReactNode;
        handleUpdate: (val: PlanFilterStatuses, active?: boolean) => void;
    }
) {
    const { component, label, text, labelDesc, active, handleUpdate, children, value, bg } = status;

    const handleClick = () => {
        handleUpdate(value, active);
    };
    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                className=" cursor-pointer relative  hover:bg-gray-200 text-start  bg-transparent flex gap-2 items-center z-10  duration-200     px-2 py-1 rounded "
            >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg " style={{ background: bg }}>
                    {createElement(component(), {
                        className: "w-4 h-4",
                        color: active ? text : "grey",
                    })}
                </div>
                <div>
                    <div className="text-base leading-5 font-medium">{label}</div>
                    <p className="text-[13px] text-gray-500">{labelDesc}</p>
                </div>
                {children}
                <NoMaxWidthTooltip
                    sx={{
                        [`& .${tooltipClasses.tooltip}`]: {
                            backgroundColor: "white",
                            border: "none",
                            padding: "0px !important",
                            fontSize: 12,
                            maxWidth: "200px !important",
                        },
                    }}
                    arrow
                    title={<HotelStatusTooltip status={status} />}
                >
                    <span className=" absolute right-1 top-[2px] ">
                        <TbInfoCircle size={12} />
                    </span>
                </NoMaxWidthTooltip>
            </button>
        </>
    );
}
